import DataTableCellField from "@/components/data-table/components/DataTableCellField";
import { DirtyAwareCell } from "@/contexts/FormTableCellDirtyContext";
import { PayrollDepartmentBasedInspection } from "@/types/payrollDepartmentBasedInspection";
import { PayrollRoleBasedInspection } from "@/types/payrollRoleBasedInspection";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  createAgreementStatusCurrent,
  createAgreementStatusPeriod,
  createContractorCardS3Key,
  createSettlementType,
  createTaxStatusCorrected,
  createTaxStatusCurrent,
  createTaxStatusFinal,
  createTaxStatusPeriod,
} from "../columns";
import { TriangleAlertIcon } from "lucide-react";
import { formatNumber } from "@/lib/utils/formatters";
import PayrollVisitCountCorrectedCell from "../components/PayrollVisitCountCorrectedCell";

export const usePayrollInspectionColumnOverrides = (
  isRoleBased: boolean,
): Record<
  string,
  ColumnDef<PayrollDepartmentBasedInspection | PayrollRoleBasedInspection>
> => {
  const { t } = useTranslation();
  return useMemo(() => {
    const commonColumns: Record<
      string,
      ColumnDef<PayrollDepartmentBasedInspection | PayrollRoleBasedInspection>
    > = {
      agreement_status_period: createAgreementStatusPeriod(t),
      agreement_status_current: createAgreementStatusCurrent(t),
      hours_worked_corrected: {
        id: "hours_worked_corrected",
        cell: ({ row }) => {
          const fieldPath = `entries.${row.original.id}.hours_worked_corrected`;
          return (
            <DirtyAwareCell fieldPath={fieldPath}>
              <DataTableCellField name={fieldPath} type="number" />
            </DirtyAwareCell>
          );
        },
        meta: {
          allowColumnToBeALink: false,
        },
      },
      deduction: {
        id: "deduction",
        cell: ({ row }) => {
          const fieldPath = `entries.${row.original.id}.deduction`;
          return (
            <DirtyAwareCell fieldPath={fieldPath}>
              <DataTableCellField name={fieldPath} type="number" />
            </DirtyAwareCell>
          );
        },
        meta: {
          allowColumnToBeALink: false,
        },
      },
      penalty: {
        id: "penalty",
        cell: ({ row }) => {
          const fieldPath = `entries.${row.original.id}.penalty`;
          return (
            <DirtyAwareCell fieldPath={fieldPath}>
              <DataTableCellField name={fieldPath} type="number" />
            </DirtyAwareCell>
          );
        },
        meta: {
          allowColumnToBeALink: false,
        },
      },
      addition: {
        id: "addition",
        cell: ({ row }) => {
          const fieldPath = `entries.${row.original.id}.addition`;
          return (
            <DirtyAwareCell fieldPath={fieldPath}>
              <DataTableCellField name={fieldPath} type="number" />
            </DirtyAwareCell>
          );
        },
        meta: {
          allowColumnToBeALink: false,
        },
      },
      comment: {
        id: "comment",
        header: t("payrolls.fields.comment_inspection"),
        cell: ({ row }) => {
          const fieldPath = `entries.${row.original.id}.comment`;
          return (
            <DirtyAwareCell fieldPath={fieldPath}>
              <DataTableCellField name={fieldPath} />
            </DirtyAwareCell>
          );
        },
        meta: {
          allowColumnToBeALink: false,
        },
      },
      permanent_comment: {
        id: "permanent_comment",
        meta: {
          allowColumnToBeALink: false,
        },
      },
      bonus: {
        id: "bonus",
        cell: ({ row }) => {
          const fieldPath = `entries.${row.original.id}.bonus`;
          return (
            <DirtyAwareCell fieldPath={fieldPath}>
              <DataTableCellField name={fieldPath} type="number" />
            </DirtyAwareCell>
          );
        },
        meta: {
          allowColumnToBeALink: false,
        },
      },
      tax_status_period: createTaxStatusPeriod(t),
      tax_status_current: createTaxStatusCurrent(t),
      tax_status_corrected: createTaxStatusCorrected(t),
      tax_status_final: createTaxStatusFinal(t),
    };

    const roleColumns = {
      settlement_type: createSettlementType(t),
      visit_count_corrected: {
        id: "visit_count_corrected",
        cell: ({ row }) => {
          const entry = row.original as PayrollRoleBasedInspection;
          return (
            <PayrollVisitCountCorrectedCell
              entryId={entry.id}
              hoursBoundToVisits={entry.hours_bound_to_visits}
              mostFrequentPlannedVisitDuration={
                entry.most_frequent_planned_visit_duration
              }
            />
          );
        },
        meta: {
          allowColumnToBeALink: false,
        },
      },
      contractor_card_s3_key: createContractorCardS3Key(),

      total_invoiced_hours_exceeded: {
        id: "total_invoiced_hours_exceeded",
        cell: ({ row }) => {
          const { total_invoiced_hours_exceeded } = row.original;
          if (parseFloat(total_invoiced_hours_exceeded) > 0) {
            return (
              <span className="flex items-center text-red-700">
                <TriangleAlertIcon className="w-4 h-4 mr-1" />
                {formatNumber(total_invoiced_hours_exceeded)}
              </span>
            );
          }
          return total_invoiced_hours_exceeded;
        },
      },
    } satisfies Record<string, ColumnDef<PayrollRoleBasedInspection>>;

    const departmentColumns = {
      cash_shortage: {
        id: "cash_shortage",
        cell: ({ row }) => {
          const fieldPath = `entries.${row.original.id}.cash_shortage`;
          return (
            <DirtyAwareCell fieldPath={fieldPath}>
              <DataTableCellField name={fieldPath} type="number" />
            </DirtyAwareCell>
          );
        },
        meta: {
          allowColumnToBeALink: false,
        },
      },
    } satisfies Record<string, ColumnDef<PayrollDepartmentBasedInspection>>;

    return {
      ...commonColumns,
      ...(isRoleBased ? roleColumns : departmentColumns),
    } as Record<
      string,
      ColumnDef<PayrollDepartmentBasedInspection | PayrollRoleBasedInspection>
    >;
  }, [isRoleBased, t]);
};
